/* eslint-disable camelcase */
import isEmpty from 'lodash/isEmpty';
import {useCallback, useEffect, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import add from 'date-fns/add';
import parseISO from 'date-fns/parseISO';
import endOfMonth from 'date-fns/endOfMonth';
import startOfMonth from 'date-fns/startOfMonth';
import {format} from 'date-fns-tz';
import {useNavigate} from 'react-router-dom';
import {v4 as uuidv4} from 'uuid';
import {cnb} from 'cnbuilder';
import IconArrowLeftDefault from '@teladoc/pulse/icons/arrow-left-default.svg';
import IconInfoDefault from '@teladoc/pulse/icons/info-default.svg';
import Button from '@teladoc/pulse/ui/Button';
import Chip from '@teladoc/pulse/ui/Chip';
import DatePicker from '@teladoc/pulse/ui/DatePicker';
import Form from '@teladoc/pulse/ui/Form';
import HorizontalRule from '@teladoc/pulse/ui/HorizontalRule';
import Label from '@teladoc/pulse/ui/Label';
import Loader from '@teladoc/pulse/ui/Loader';
import Modal from '@teladoc/pulse/ui/Modal';
import TextArea from '@teladoc/pulse/ui/TextArea';
import Select from '@teladoc/pulse/ui/Select';
import {useI18nContext} from '@teladoc/pulse/ui/Context/i18n';
import Arg from '@livongo/arg';
import useTransLoader from 'i18n/use-trans-loader';
import AcuityError from '../ErrorMessages/AcuityError';
import {goToMyStrength} from '../utils/myStrength';
import {
    axleTimeZones,
    defaultTimeZoneAxle,
    localizedTimeAxle,
} from '../utils/Timezone';
import {MH_MIXPANEL_PRODUCT, IS_PROD} from '../config';
import AxleAPI from '../utils/Axle';
import UserInfoAPI from '../UserInfo/userInfo-api';
import MixpanelUtils from '../common/mix-panel';
import css from './SchedulingFormMentalHealth.scss';
import '../index.scss';

const METADATA_FIELD_LABELS = {
    comments: 'Comments',
    assistiveDevice: 'Do you use assistive devices?',
    videoCamera: 'Do you have a video camera?',
    uuid: 'UUID',
    sessionMediaType: 'How would you like to connect?',
};

const SchedulingFormMentalHealthAxle = () => {
    const {t} = useTransLoader('schedulingForm');
    const {selectedCountry, selectedLang} = useI18nContext();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {token, uuid} = useSelector(state => state.userData.auth);
    const {firstName, lastName, email, phones, birthDate} = useSelector(
        state => state.userData.userData
    );
    const [assistiveDevice, setAsstistiveDevice] = useState('No');
    const [videoCamera, setVideoCamera] = useState('Yes');
    const [sessionMediaType, setSessionMediaType] = useState('VIDEO');
    const [internalUserId, setInternalUserId] = useState(null);
    const [APIError, setAPIError] = useState();
    const [selectedTimezone, setSelectedTimezone] = useState(
        defaultTimeZoneAxle(selectedCountry)
    );
    const [assignedCoachName, setAssignedCoachName] = useState('');
    const [selectedAssignedCoach, setSelectedAssignedCoach] = useState(true);
    const [selectedAnyCoach, setSelectedAnyCoach] = useState(false);
    const [availableDates, setAvailableDates] = useState([]);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [selectedTimeSlotData, setSelectedTimeSlotData] = useState(null);
    const [availableTimesForDate, setAvailableTimesForDate] = useState({});
    const [timeSlotDetails, setTimeSlotDetails] = useState({});
    const [hasUpcomingSession, setHasUpcomingSession] = useState(false);
    const [coachId, setCoachId] = useState(null);
    const [notes, setNotes] = useState('');
    const [timeKey, setTimeKey] = useState(uuidv4());
    const [showSwitchingToNewCoachModal, setShowSwitchingToNewCoachModal] =
        useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [datePickerKey, setDatePickerKey] = useState(uuidv4());
    const [isAlreadyBooked, setIsAlreadyBooked] = useState(null);
    const isOneApp = Arg('oneAppSdk');
    const showSessionMediaTypeInput = Arg('showSessionMediaTypeInput');
    let assignedCoachId = Arg('staffId');
    const rootClass = cnb(css.root, {
        [css.oneapp]: isOneApp,
    });
    const buttonClass = cnb(css.confirmButton, {
        [css.oneapp]: isOneApp,
    });
    const serviceIdParam = Arg('serviceId'); // Note: myS will continue passing the TimeTap serviceIds
    const serviceId = '232464';
    const isInitial = serviceIdParam === 689438 || serviceIdParam === 688157;

    // TODO: Remove manual mapping for lower env test coach ID
    if (!IS_PROD) {
        assignedCoachId = '324657';
    }

    const onTimezoneChange = timezone => {
        setSelectedTimezone(timezone);

        setAvailableTimes(
            availableTimes.map(timeSlot => ({
                label: localizedTimeAxle(
                    timeSlot.value,
                    timezone.value,
                    selectedLang
                ),
                value: timeSlot.value,
            }))
        );
    };

    const getMemberMHSessions = useCallback(() => {
        AxleAPI.getMemberMHSessions(token).then(data => {
            const upcomingSessions = data?.sessions?.filter(
                session => session.status === 'SCHEDULED'
            );

            if (!isEmpty(upcomingSessions)) {
                setHasUpcomingSession(true);
            }
        });
    }, [token]);

    const getMHCoachName = useCallback(() => {
        AxleAPI.getMHCoachName(token)
            .then(data => {
                setAssignedCoachName(data?.name ?? '');
            })
            .catch(() => setAssignedCoachName(''));
    }, [token]);

    const processAvailabilityData = useCallback(
        availabilityData => {
            const datesSet = new Set();
            const timesForDateMap = {};
            const timeSlotProviderMap = {};

            availabilityData.providers.forEach(provider => {
                // Add safety check for available_slots
                if (
                    provider.available_slots &&
                    Array.isArray(provider.available_slots)
                ) {
                    provider.available_slots.forEach(startDateTime => {
                        const slotTime = new Date(startDateTime);
                        const now = new Date();
                        const isToday =
                            format(slotTime, 'yyyy-MM-dd') ===
                            format(now, 'yyyy-MM-dd');

                        if (isToday) {
                            const twoHoursFromNow = new Date(
                                now.getTime() + 2 * 60 * 60 * 1000
                            );

                            if (slotTime < twoHoursFromNow) {
                                // Skip this slot - not enough advance notice
                                return;
                            }
                        }
                        const dateKey = format(slotTime, 'MM/dd/yyyy');
                        const timeIdentifier = startDateTime;

                        datesSet.add(dateKey);

                        // Initialize time slot entry if it doesn't exist
                        if (!timeSlotProviderMap[timeIdentifier]) {
                            timeSlotProviderMap[timeIdentifier] = {
                                providers: [],
                                start_dt_time: startDateTime,
                            };

                            // Initialize array for this date if it doesn't exist
                            if (!timesForDateMap[dateKey]) {
                                timesForDateMap[dateKey] = [];
                            }

                            // Add time option for this date (only once per unique time)
                            timesForDateMap[dateKey].push({
                                label: localizedTimeAxle(
                                    startDateTime,
                                    selectedTimezone?.value,
                                    selectedLang
                                ),
                                value: timeIdentifier,
                            });
                        }

                        // Add provider to this time slot
                        timeSlotProviderMap[timeIdentifier].providers.push({
                            provider_id: provider.provider_id,
                            activeAppointmentCount:
                                provider.activeAppointmentCount,
                        });
                    });
                }
            });

            // Rest of the function remains the same...
            const sortedDates = Array.from(datesSet).sort((a, b) => {
                return new Date(a) - new Date(b);
            });

            Object.keys(timesForDateMap).forEach(date => {
                timesForDateMap[date].sort((a, b) => {
                    const timeA = timeSlotProviderMap[a.value]?.start_dt_time;
                    const timeB = timeSlotProviderMap[b.value]?.start_dt_time;

                    return new Date(timeA) - new Date(timeB);
                });
            });

            setAvailableDates(sortedDates);
            setAvailableTimesForDate(timesForDateMap);
            setTimeSlotDetails(timeSlotProviderMap);
        },
        [selectedTimezone, selectedLang]
    );

    const getCoachAvailableDates = useCallback(async () => {
        try {
            const today = new Date();
            const expirationDateParam = Arg('expirationDate');
            const expirationDate = expirationDateParam
                ? parseISO(expirationDateParam)
                : null;
            const requests = [];
            const monthsAhead = 2;

            for (let i = 0; i <= monthsAhead; i++) {
                const targetMonth = i === 0 ? today : add(today, {months: i});
                const startDate = i === 0 ? today : startOfMonth(targetMonth);
                let endDate = endOfMonth(targetMonth);

                // Only process if month doesn't start after expiration
                if (!expirationDate || startDate <= expirationDate) {
                    // Adjust end date if expiration falls within this month
                    if (
                        expirationDate &&
                        expirationDate < endDate &&
                        expirationDate >= startDate
                    ) {
                        endDate = expirationDate;
                    }

                    requests.push(
                        AxleAPI.getCoachAvailability({
                            token,
                            startDate: startDate.toISOString(),
                            endDate: endDate.toISOString(),
                            reasonCode: serviceId,
                            locale: selectedLang,
                            type: isInitial ? 'INITIAL' : 'FOLLOW_UP',
                            providerId: assignedCoachId,
                        })
                    );
                }
            }

            const results = await Promise.all(requests);
            const allData = {
                providers: results.flatMap(result => result.providers),
            };

            processAvailabilityData(allData);
        } catch (error) {
            setAPIError(error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const getAllCoachesAvailableDates = useCallback(async () => {
        try {
            const today = new Date();
            const expirationDateParam = Arg('expirationDate');
            const expirationDate = expirationDateParam
                ? parseISO(expirationDateParam)
                : null;
            const requests = [];
            const monthsAhead = 2;

            for (let i = 0; i <= monthsAhead; i++) {
                const targetMonth = i === 0 ? today : add(today, {months: i});
                const startDate = i === 0 ? today : startOfMonth(targetMonth);
                let endDate = endOfMonth(targetMonth);

                // Only process if month doesn't start after expiration
                if (!expirationDate || startDate <= expirationDate) {
                    // Adjust end date if expiration falls within this month
                    if (
                        expirationDate &&
                        expirationDate < endDate &&
                        expirationDate >= startDate
                    ) {
                        endDate = expirationDate;
                    }

                    requests.push(
                        AxleAPI.getAllCoachesAvailability({
                            token,
                            startDate: startDate.toISOString(),
                            endDate: endDate.toISOString(),
                            reasonCode: serviceId,
                            locale: selectedLang,
                            type: isInitial ? 'INITIAL' : 'FOLLOW_UP',
                        })
                    );
                }
            }

            const results = await Promise.all(requests);
            const allData = {
                providers: results.flatMap(result => result.providers),
            };

            processAvailabilityData(allData);
        } catch (error) {
            setAPIError(error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Trigerring a re-render for the time selection so that it shows the placeholder text for the new day selected
    const renderTimeSelection = () => {
        setTimeKey(uuidv4());
        setAvailableTimes([]);
    };

    const renderDayAndTimeSelection = () => {
        // Trigerring a re-render for the calendar so that it shows the
        // placeholder text instead of the selected day for the previous coach
        setDatePickerKey(uuidv4());
        setAvailableDates([]);

        renderTimeSelection();
    };

    const onDaySelected = date => {
        const dateInstance = new Date(date);
        const dateKey = format(dateInstance, 'MM/dd/yyyy');

        if (coachId) {
            setCoachId(null);
        }

        if (availableTimes) {
            renderTimeSelection();
        }

        const timesForDate = availableTimesForDate[dateKey] || [];

        const formattedTimes = timesForDate.map(timeSlot => ({
            label: localizedTimeAxle(
                timeSlot.value,
                selectedTimezone?.value,
                selectedLang
            ),
            value: timeSlot.value,
        }));

        setAvailableTimes(formattedTimes);
    };

    const onTimeSelected = time => {
        const timeSlotInfo = timeSlotDetails[time.value];

        if (timeSlotInfo && timeSlotInfo?.providers.length > 0) {
            const selectedProvider = timeSlotInfo.providers.reduce(
                (prev, current) =>
                    prev.activeAppointmentCount < current.activeAppointmentCount
                        ? prev
                        : current
            );

            setSelectedTimeSlotData({
                provider_id: selectedProvider?.provider_id,
                start_dt_time: timeSlotInfo?.start_dt_time,
            });

            setCoachId(selectedProvider?.provider_id);
        }
    };

    const onSelectedAssignedCoach = () => {
        setSelectedAnyCoach(false);
        setSelectedAssignedCoach(true);

        renderDayAndTimeSelection();
    };

    const onSelectedAnyCoach = () => {
        setSelectedAnyCoach(true);
        setSelectedAssignedCoach(false);

        renderDayAndTimeSelection();
        getAllCoachesAvailableDates();
    };

    const scheduleAppointment = () => {
        setIsSaving(true);

        const isNewCoach =
            selectedAnyCoach && coachId !== Number(assignedCoachId);
        const appointmentData = {
            member: {
                name: `${firstName} ${lastName}`,
                id: internalUserId,
                timezone_cd: selectedTimezone?.value,
                email,
                phone: phones?.[0]?.numberE164,
                dob: format(new Date(birthDate), 'MM-dd-yyyy'),
                sex: 'U',
            },
            start_date_time: selectedTimeSlotData?.start_dt_time,
            communication_method: sessionMediaType,
            reason_code: serviceId,
            appointment_type: isInitial ? 'INITIAL' : 'FOLLOW_UP',
            member_locale: selectedLang,
            duration_minutes: 30,
            appointment_status: 'SCHEDULED',
            provider: {
                provider_id: String(selectedTimeSlotData?.provider_id),
                system: 'MYSTRENGTH',
            },
            notes,
            metadata: {
                [METADATA_FIELD_LABELS.assistiveDevice]: assistiveDevice,
                ...(showSessionMediaTypeInput
                    ? {
                          [METADATA_FIELD_LABELS.sessionMediaType]:
                              sessionMediaType,
                      }
                    : {[METADATA_FIELD_LABELS.videoCamera]: videoCamera}),
                UUID: uuid,
            },
        };

        AxleAPI.createAppointment(token, appointmentData)
            .then(data => {
                setIsSaving(false);
                setShowSwitchingToNewCoachModal(false);

                dispatch({
                    type: 'APPOINTMENT_DETAILS',
                    payload: {
                        startDate: new Date(
                            selectedTimeSlotData?.start_dt_time
                        ),
                        endDate: add(
                            parseISO(selectedTimeSlotData?.start_dt_time),
                            {
                                minutes: 30,
                            }
                        ),
                        timezone: selectedTimezone?.value,
                        type: isInitial
                            ? 'Mental Health- Initial Coaching Session'
                            : 'Mental Health- Follow-up Coaching Session',
                        coach: data?.provider_full_name,
                        isNewCoach,
                    },
                });

                MixpanelUtils.track({
                    event: 'coaching.scheduling.success',
                    properties: {
                        // eslint-disable-next-line camelcase
                        product_line: MH_MIXPANEL_PRODUCT,
                    },
                });

                navigate('/confirmationMentalHealth');
            })
            .catch(error => {
                setShowSwitchingToNewCoachModal(false);
                setIsSaving(false);
                setAPIError(error);

                MixpanelUtils.track({
                    event: 'coaching.scheduling.failure',
                    properties: {
                        // eslint-disable-next-line camelcase
                        product_line: MH_MIXPANEL_PRODUCT,
                        'Error Code': error.status,
                    },
                });
            });
    };

    const onScheduleSessionClick = () => {
        if (selectedAnyCoach && coachId !== Number(assignedCoachId)) {
            setShowSwitchingToNewCoachModal(true);
        } else {
            scheduleAppointment();
        }
    };

    const onModalClose = () => {
        setShowSwitchingToNewCoachModal(false);
    };

    useEffect(() => {
        if (token && uuid) {
            UserInfoAPI.schedulingData(token, uuid)
                .then(data => {
                    dispatch({
                        type: 'USER_DATA',
                        payload: data,
                    });
                })
                .catch(({data}) => {
                    dispatch({
                        type: 'ERROR',
                        payload: JSON.stringify(data),
                    });
                    dispatch(UserInfoAPI.incorrectToken);
                });
            UserInfoAPI.getInternalUserId(token, uuid)
                .then(data => setInternalUserId(data?.uuid))
                .catch(err => {
                    setAPIError(err);
                });
        }
    }, [token, uuid, dispatch]);

    useEffect(() => {
        if (token) {
            getMemberMHSessions();
            getMHCoachName();
        }
    }, [token, getMemberMHSessions, getMHCoachName]);

    useEffect(() => {
        if (token && serviceId) {
            if (!selectedAnyCoach) {
                getCoachAvailableDates();
            }
        }
    }, [token, serviceId, selectedAnyCoach, getCoachAvailableDates]);

    useEffect(() => {
        if (APIError) {
            if (APIError?.data?.error?.code?.includes('409')) {
                setIsAlreadyBooked(true);
            }

            MixpanelUtils.track({
                event: 'coaching.scheduling.error',
                properties: {
                    // eslint-disable-next-line camelcase
                    product_line: MH_MIXPANEL_PRODUCT,
                    'Error Code': APIError.status,
                },
            });
        }
    }, [APIError]);

    useEffect(() => {
        MixpanelUtils.track({
            event: 'coaching.scheduling.viewed',
            properties: {
                // eslint-disable-next-line camelcase
                product_line: MH_MIXPANEL_PRODUCT,
            },
        });
    }, []);

    return (
        <div className={rootClass}>
            <AcuityError
                isOpen={APIError}
                toggleModal={setAPIError}
                isAlreadyBooked={isAlreadyBooked}
            />
            <h2 className={css.subheading}>{t('title')}</h2>
            <Form>
                <div>
                    <h3 className={css.sectionTitle}>
                        {t('myStrength.details')}
                    </h3>
                    <Select
                        id="haveAssistiveDevice"
                        label={<Label>{t('myStrength.device')}</Label>}
                        items={[
                            {
                                label: t('myStrength.yes'),
                                value: t('myStrength.yes'),
                            },
                            {
                                label: t('myStrength.no'),
                                value: t('myStrength.no'),
                            },
                        ]}
                        onChange={({value}) => setAsstistiveDevice(value)}
                    />
                    {showSessionMediaTypeInput ? (
                        <Select
                            id="haveCamera"
                            label={
                                <Label>
                                    {t('myStrength.connectType.label')}
                                </Label>
                            }
                            items={[
                                {
                                    label: t('myStrength.connectType.video'),
                                    value: 'VIDEO',
                                },
                                {
                                    label: t('myStrength.connectType.phone'),
                                    value: 'PHONE',
                                },
                            ]}
                            onChange={({value}) => setSessionMediaType(value)}
                        />
                    ) : (
                        <Select
                            id="haveCamera"
                            label={<Label>{t('myStrength.video')}</Label>}
                            items={[
                                {
                                    label: t('myStrength.yes'),
                                    value: t('myStrength.yes'),
                                },
                                {
                                    label: t('myStrength.no'),
                                    value: t('myStrength.no'),
                                },
                            ]}
                            onChange={({value}) => setVideoCamera(value)}
                        />
                    )}
                    <HorizontalRule spaceTop={24} spaceBottom={24} />
                </div>
                <div>
                    <h3 className={css.sectionTitle}>
                        {t('sessionInfo.mentalHealthHeading')}
                    </h3>
                    <span className={css.selectCoachHeading}>
                        {t('sessionInfo.availability')}
                    </span>
                    {assignedCoachName ? (
                        <div className={css.chipsContainer}>
                            <Chip
                                id="coach"
                                label={assignedCoachName}
                                checked={selectedAssignedCoach}
                                onChange={() => onSelectedAssignedCoach()}
                            />
                            {!hasUpcomingSession && (
                                <Chip
                                    classNameRoot={css.chip}
                                    id="anyCoach"
                                    label={t('sessionInfo.anyCoach')}
                                    checked={selectedAnyCoach}
                                    onChange={() => onSelectedAnyCoach()}
                                />
                            )}
                        </div>
                    ) : (
                        <Loader classNameRoot={css.loader} />
                    )}
                    {selectedAnyCoach && (
                        <div className={css.infoBanner}>
                            <div className={css.infoBannerHeadingContainer}>
                                <IconInfoDefault className={css.iconInfo} />
                                <p>{t('sessionInfo.infoBanner.heading')}</p>
                            </div>
                            <p className={css.infoBannerDescription}>
                                {t('sessionInfo.infoBanner.description')}
                            </p>
                        </div>
                    )}
                    <Select
                        id="timeZone"
                        label={<Label>{t('sessionInfo.timezone')}</Label>}
                        placeholder={t('sessionInfo.timezonePlaceholder')}
                        items={axleTimeZones[selectedCountry].map(
                            ({value, label}) => ({
                                value,
                                label: t(`timeZone.${label}`),
                            })
                        )}
                        defaultValue={selectedTimezone?.value}
                        onChange={timezone => onTimezoneChange(timezone)}
                        required
                    />
                    <DatePicker
                        key={datePickerKey}
                        id="date"
                        name="date"
                        classNameItem={css.dateInput}
                        calendar={{
                            disablePast: true,
                            available: availableDates,
                        }}
                        label={<Label> {t('sessionInfo.date')}</Label>}
                        disabled={isEmpty(availableDates)}
                        onChange={date => onDaySelected(date)}
                        required
                    />
                    <Select
                        key={timeKey}
                        id="time"
                        name="time"
                        disabled={isEmpty(availableTimes)}
                        label={<Label> {t('sessionInfo.time')}</Label>}
                        items={availableTimes}
                        onChange={time => onTimeSelected(time)}
                        placeholder={t('sessionInfo.timePlaceholder')}
                        required
                    />
                    <HorizontalRule spaceTop={24} spaceBottom={24} />
                </div>
                <TextArea
                    id="note"
                    name="note"
                    maxLength={255}
                    showCounter
                    label={
                        <Label className={css.sectionTitle}>
                            {t('sessionInfo.notes')}
                        </Label>
                    }
                    placeholder={t('sessionInfo.notesPlaceholder')}
                    onChange={e => setNotes(e.target.value)}
                />
                {!isOneApp && (
                    <Button
                        variant="secondary"
                        className={css.backButton}
                        onClick={goToMyStrength}
                    >
                        <IconArrowLeftDefault />
                    </Button>
                )}
                <div>
                    <Button
                        type="submit"
                        variant="primary"
                        className={buttonClass}
                        disabled={isSaving}
                        loading={isSaving}
                        onClick={onScheduleSessionClick}
                    >
                        {t('sessionInfo.submit')}
                    </Button>
                </div>
                <Modal
                    classNameContainer={css.modalTitle}
                    isOpen={showSwitchingToNewCoachModal}
                    title={<h2>{t('sessionInfo.infoBanner.heading')}</h2>}
                    onRequestClose={onModalClose}
                    primaryAction={{
                        variant: 'primary',
                        onClick: scheduleAppointment,
                        children: t('sessionInfo.confirm'),
                    }}
                    secondaryAction={{
                        variant: 'secondary',
                        onClick: onModalClose,
                        children: t('sessionInfo.cancel'),
                    }}
                >
                    <p>{t('sessionInfo.infoBanner.description')}</p>
                </Modal>
            </Form>
        </div>
    );
};

export default SchedulingFormMentalHealthAxle;
